/**
 * Вода канона → формы экранов `/water` и `/analysis/water`.
 *
 * В отличие от остальных адаптеров этого каталога, здесь НЕТ набора с именем
 * файла прототипа: водный журнал прототипа был устроен по работам цикла
 * (`water_row.work_id`), а канон — по заявкам хозяйства и подачам канала.
 * Подогнать одно под другое значило бы сочинить связь «заявка → работа»,
 * которой у заявки нет. Поэтому экраны воды на каноне — отдельные компоненты,
 * а этот модуль только переименовывает поля трёх ответов (`/water/ledger`,
 * `/water/balance`, `/water/works/{id}`) и списка поливов из картины сезона.
 *
 * Два правила, ради которых модуль существует отдельно от JSX:
 *
 * * **объём без числа остаётся без числа.** Десятичная строка канона
 *   переводится в число, а `null` («не измерено», «записей нет») остаётся
 *   `null`: ноль кубометров означал бы, что воду подавали и подали нисколько;
 * * **ничего не пересчитывается.** Баланс, сверку и счёт закрытых
 *   обязательств считает сервер; здесь их только раскладывают по полям.
 *   Хозяйства полива, «канал → хозяйства», недоподача (правило №222) и доля
 *   «подано от запрошенного» тоже приходят готовыми (С4): клиентской арифметики
 *   над м³ здесь нет.
 */

import type {
  SeasonCycles,
  SeasonPicture,
  WaterAllocationRow,
  WaterBalance,
  WaterChannelRow,
  WaterCycle,
  WaterDeliveryRow,
  WaterFarmRow,
  WaterLedger,
  WaterRequestRow,
  WaterWork,
} from '../../../api/client';
import { decimal, toHa } from './dataset';
import { cropName } from '../../../lib/crops';
import { defineMessages } from '../../../i18n/define';
import { tr } from '../../../i18n/runtime';

const m = defineMessages({
  noName: {
    id: 'data.water.noName',
    defaultMessage: 'имя не названо',
    description: 'Вместо названия хозяйства в строке водного баланса, когда сервер имя не назвал (строчная фраза)',
  },
});


/** Демо-строка — не факт; экран обязан это пометить, а не смешать с реальным. */
function isDemo(status: string): boolean {
  return status === 'synthetic_demo';
}

/* ---------------------------------------------------------------- баланс */

export interface WaterBalanceView {
  requests: number;
  lines: number;
  deliveries: number;
  allocations: number;
  /** `null` — объёма в срезе нет, а не «ноль кубометров». */
  requestedM3: number | null;
  deliveredM3: number | null;
  unmeasuredLines: number;
  unmeasuredAllocations: number;
  obligationsClosed: number;
  obligationsUnplanned: number;
  obligationsOpen: number;
  matched: number;
  mismatched: number;
  unverifiable: number;
  outcome: WaterBalance['outcome'];
  /** Причина итога словами сервера — печатается как есть. */
  outcomeReason: string;
  /** Подано от запрошенного, % — считает сервер; `null` — делить не на что. */
  coveragePct: number | null;
}

export function toBalance(balance: WaterBalance): WaterBalanceView {
  // Ключи сверки приходят словарём: отсутствующий ключ — «таких подач нет»,
  // это счётчик, а не измерение, поэтому ноль здесь честен.
  const rec = balance.reconciliation ?? {};
  return {
    requests: balance.requests,
    lines: balance.lines,
    deliveries: balance.deliveries,
    allocations: balance.allocations,
    requestedM3: decimal(balance.requested_volume_m3),
    deliveredM3: decimal(balance.delivered_volume_m3),
    unmeasuredLines: balance.unmeasured_lines,
    unmeasuredAllocations: balance.unmeasured_allocations,
    obligationsClosed: balance.obligations_closed,
    obligationsUnplanned: balance.obligations_unplanned,
    obligationsOpen: balance.obligations_open,
    matched: rec.matched ?? 0,
    mismatched: rec.mismatched ?? 0,
    unverifiable: rec.unverifiable ?? 0,
    outcome: balance.outcome,
    outcomeReason: balance.outcome_reason,
    coveragePct: balance.coverage_pct ?? null,
  };
}

/* ---------------------------------------------------------- журнал заявок */

export interface LedgerRow {
  requestId: number;
  actorId: number;
  /** `null` — сервер имени не дал; экран говорит это словами. */
  actorName: string | null;
  submittedOn: string;
  desiredFrom: string | null;
  desiredTo: string | null;
  lines: number;
  contours: number;
  /** Гектары; `null` — площадь позиций не названа. */
  areaHa: number | null;
  linesWithoutArea: number;
  requestedM3: number | null;
  deliveredM3: number | null;
  unmeasuredLines: number;
  unmeasuredAllocations: number;
  deliveries: number;
  closedObligations: number;
  demo: boolean;
}

export interface LedgerView {
  rows: LedgerRow[];
  page: { limit: number; offset: number; total: number };
  balance: WaterBalanceView;
  /** Объяснение пустого списка от сервера; при непустом — пустая строка. */
  emptyNote: string;
}

export function toLedgerRow(row: WaterRequestRow): LedgerRow {
  return {
    requestId: row.request_id,
    actorId: row.actor.id,
    actorName: row.actor.name,
    submittedOn: row.submitted_on,
    desiredFrom: row.desired_from,
    desiredTo: row.desired_to,
    lines: row.lines,
    contours: row.contours,
    areaHa: toHa(row.area_microha),
    linesWithoutArea: row.lines_without_area,
    requestedM3: decimal(row.requested_volume_m3),
    deliveredM3: decimal(row.delivered_volume_m3),
    unmeasuredLines: row.unmeasured_lines,
    unmeasuredAllocations: row.unmeasured_allocations,
    deliveries: row.deliveries,
    closedObligations: row.closed_obligations,
    demo: isDemo(row.epistemic_status),
  };
}

export function toLedger(reply: WaterLedger): LedgerView {
  return {
    rows: reply.items.map(toLedgerRow),
    page: reply.page,
    balance: toBalance(reply.balance),
    emptyNote: reply.empty_note,
  };
}

/* ------------------------------------------------------- полив до подач */

export interface DeliveryView {
  deliveryId: number;
  channel: string;
  massifId: number | null;
  massifName: string | null;
  periodFrom: string;
  periodTo: string | null;
  totalM3: number | null;
  method: string;
  allocations: number;
  reconciliation: WaterDeliveryRow['reconciliation'];
  reconciliationReason: string;
  /** `null` — разницу не с чем считать, а не «разницы нет». */
  differenceM3: number | null;
  obligationsClosed: number;
  demo: boolean;
}

export interface AllocationView {
  allocationId: number;
  deliveryId: number;
  contourId: number;
  contourNo: string;
  actorId: number;
  /** `null` — сервер имени не дал; экран говорит это словами. */
  actorName: string | null;
  areaHa: number | null;
  requestedM3: number | null;
  deliveredM3: number | null;
  /** Период подачи — из операции, на которую легла строка; нет операции — `null`. */
  periodFrom: string | null;
  periodTo: string | null;
  obligationId: number | null;
  demo: boolean;
}

/** Положение хозяйства в поливе: подано полностью, частично, не подано, не измерено. */
export type FarmWaterState = 'delivered' | 'partial' | 'none' | 'unmeasured';

/** Хозяйство полива — строка сервера (правило №222 уже применено). */
export interface FarmWaterRow {
  actorId: number;
  name: string;
  contourIds: string[];
  /** `null` — ни одна позиция не назвала объём; это не ноль. */
  requestedM3: number | null;
  deliveredM3: number | null;
  /** Недоподача; `null` — не посчитать. */
  shortM3: number | null;
  /** Перерасход; `null` — не посчитать. */
  overM3: number | null;
  coveragePct: number | null;
  state: FarmWaterState;
}

/** Контур полива: запрошено, подано и состояние для слоя карты — строка сервера. */
export interface ContourWaterRow {
  contourId: number;
  contourNo: string;
  actorName: string | null;
  requestedM3: number | null;
  deliveredM3: number | null;
  state: FarmWaterState;
}

/** Недоподача набора хозяйств — сумма положительных разрывов (№222). */
export interface Shortfall {
  underM3: number | null;
  overM3: number | null;
  /** Хозяйства, у которых разрыв не посчитать. */
  unknown: number;
  underFarms: number;
  overFarms: number;
}

/** Канал полива и его хозяйства — «канал → хозяйства» (решение №651). */
export interface ChannelRow {
  /** Ключ канала — подпись подачи как есть (`?channel=` с «Обзора»). */
  key: string;
  label: string;
  requestedM3: number | null;
  deliveredM3: number | null;
  coveragePct: number | null;
  contourIds: string[];
  /** Хозяйства канала с теми же полями, что в общем списке; у канала кабинета поля нет. */
  farms?: FarmWaterRow[];
  shortfall?: Shortfall;
  /** Контуры канала — для карты и таблицы выбранного канала. */
  contours?: ContourWaterRow[];
}

export interface WaterWorkView {
  workDefId: number | null;
  name: string;
  deliveries: DeliveryView[];
  allocations: AllocationView[];
  balance: WaterBalanceView;
  emptyNote: string;
  /** Хозяйства полива, каналы и недоподача — с сервера (С4). */
  farms: FarmWaterRow[];
  channels: ChannelRow[];
  shortfall: Shortfall | null;
  contours: ContourWaterRow[];
}

function toContourRow(row: WaterWork['contours'][number]): ContourWaterRow {
  return {
    contourId: row.contour_id,
    contourNo: row.contour_no,
    actorName: row.actor.name,
    requestedM3: decimal(row.requested_m3),
    deliveredM3: decimal(row.delivered_m3),
    state: row.state,
  };
}

function toFarmRow(row: WaterFarmRow): FarmWaterRow {
  return {
    actorId: row.actor.id,
    name: row.actor.name ?? tr(m.noName),
    contourIds: row.contour_ids.map(String),
    requestedM3: decimal(row.requested_m3),
    deliveredM3: decimal(row.delivered_m3),
    shortM3: decimal(row.short_m3),
    overM3: decimal(row.over_m3),
    coveragePct: row.coverage_pct,
    state: row.state,
  };
}

function toShortfall(row: WaterWork['shortfall']): Shortfall | null {
  if (!row) return null;
  return {
    underM3: decimal(row.under_m3),
    overM3: decimal(row.over_m3),
    unknown: row.unknown,
    underFarms: row.under_farms,
    overFarms: row.over_farms,
  };
}

function toChannelRow(row: WaterChannelRow): ChannelRow {
  return {
    key: row.key,
    label: row.label,
    requestedM3: decimal(row.requested_m3),
    deliveredM3: decimal(row.delivered_m3),
    coveragePct: row.coverage_pct,
    contourIds: row.contour_ids.map(String),
    farms: row.farms.map(toFarmRow),
    shortfall: toShortfall(row.shortfall) ?? undefined,
    contours: (row.contours ?? []).map(toContourRow),
  };
}

export function toDelivery(row: WaterDeliveryRow): DeliveryView {
  return {
    deliveryId: row.delivery_id,
    channel: row.channel_label,
    massifId: row.massif?.id ?? null,
    massifName: row.massif?.canonical_name ?? null,
    periodFrom: row.period_from,
    periodTo: row.period_to,
    totalM3: decimal(row.total_volume_m3),
    method: row.method,
    allocations: row.allocations,
    reconciliation: row.reconciliation,
    reconciliationReason: row.reconciliation_reason,
    differenceM3: decimal(row.difference_m3),
    obligationsClosed: row.obligations_closed,
    demo: isDemo(row.epistemic_status),
  };
}

export function toAllocation(
  row: WaterAllocationRow,
  deliveries: ReadonlyMap<number, DeliveryView>,
): AllocationView {
  const delivery = deliveries.get(row.delivery_id);
  return {
    allocationId: row.allocation_id,
    deliveryId: row.delivery_id,
    contourId: row.contour_id,
    contourNo: row.contour_no,
    actorId: row.actor.id,
    actorName: row.actor.name,
    areaHa: toHa(row.area_microha),
    requestedM3: decimal(row.requested_volume_m3),
    deliveredM3: decimal(row.volume_m3),
    periodFrom: delivery?.periodFrom ?? null,
    periodTo: delivery?.periodTo ?? null,
    obligationId: row.obligation_id,
    demo: isDemo(row.epistemic_status),
  };
}

export function toWaterWork(reply: WaterWork): WaterWorkView {
  const deliveries = reply.deliveries.map(toDelivery);
  const byId = new Map(deliveries.map((d) => [d.deliveryId, d]));
  return {
    workDefId: reply.work.work_def_id,
    name: reply.work.name,
    deliveries,
    allocations: reply.allocations.map((a) => toAllocation(a, byId)),
    balance: toBalance(reply.balance),
    emptyNote: reply.empty_note,
    farms: (reply.farms ?? []).map(toFarmRow),
    channels: (reply.channels ?? []).map(toChannelRow),
    shortfall: toShortfall(reply.shortfall),
    contours: (reply.contours ?? []).map(toContourRow),
  };
}

/* ------------------------------------------------ вода цикла (сервер, С4) */

export interface CycleWorkWater {
  campaignId: number;
  workDefId: number;
  requestedM3: number | null;
  deliveredM3: number | null;
  /** Подано от запрошенного, % — с сервера. */
  coveragePct: number | null;
  unplanned: number;
}

export interface CycleMismatch {
  deliveryId: number;
  campaignId: number;
  workDefId: number;
  channel: string;
  periodFrom: string;
  periodTo: string | null;
  totalM3: number | null;
  differenceM3: number | null;
  reason: string;
}

/** Вода цикла: подачи, расхождения сверки, хозяйства цикла — всё посчитано сервером. */
export interface CycleWaterView {
  deliveries: number;
  mismatched: number;
  unplanned: number;
  /** По ключу полива (`campaign:workDef`). */
  works: Map<string, CycleWorkWater>;
  mismatches: CycleMismatch[];
  farms: FarmWaterRow[];
  shortfall: Shortfall | null;
}

export function toCycleWater(reply: WaterCycle): CycleWaterView {
  return {
    deliveries: reply.deliveries,
    mismatched: reply.mismatched,
    unplanned: reply.unplanned,
    works: new Map(
      reply.works.map((w) => [
        `${w.campaign_id}:${w.work_def_id}`,
        {
          campaignId: w.campaign_id,
          workDefId: w.work_def_id,
          requestedM3: decimal(w.requested_m3),
          deliveredM3: decimal(w.delivered_m3),
          coveragePct: w.coverage_pct,
          unplanned: w.unplanned,
        },
      ]),
    ),
    mismatches: reply.mismatches.map((m) => ({
      deliveryId: m.delivery_id,
      campaignId: m.campaign_id,
      workDefId: m.work_def_id,
      channel: m.channel_label,
      periodFrom: m.period_from,
      periodTo: m.period_to,
      totalM3: decimal(m.total_volume_m3),
      differenceM3: decimal(m.difference_m3),
      reason: m.reason,
    })),
    farms: reply.farms.map(toFarmRow),
    shortfall: toShortfall(reply.shortfall),
  };
}

/* ------------------------------------------------- поливы и циклы сезона */

type CampaignWork = NonNullable<SeasonPicture['summary']>['works'][number];

export interface IrrigationWork {
  campaignId: number;
  workDefId: number;
  name: string;
  windowFrom: string | null;
  windowTo: string | null;
  status: CampaignWork['status'];
  demo: boolean;
  /** Обязательства полива на контурах — знаменатель двух осей. */
  obligations: number;
  /** По скольким обязательствам поступил отчёт. */
  reported: number;
  /** Без отчёта — число сервера (`totals.no_report`); `null` — не посчитано. */
  noReport: number | null;
  /** Площадь обязательств полива, га; `null` — не названа. */
  areaHa: number | null;
  /** Дней до конца окна от даты состояния (сервер); `null` — окна нет. */
  daysLeft: number | null;
}

/**
 * Поливы цикла — работы категории `irrigation` из сводки картины сезона.
 * Порядок — по началу окна: это хронология поливов, а не порядок ответа.
 * Работа без окна уходит в конец, а не встаёт «в начало сезона».
 */
export function irrigationWorks(picture: SeasonPicture): IrrigationWork[] {
  const works = picture.summary?.works ?? [];
  return works
    .filter((w) => w.category === 'irrigation')
    .map((w) => ({
      campaignId: w.campaign_id,
      workDefId: w.work_def_id,
      name: w.name,
      windowFrom: w.window_from,
      windowTo: w.window_to,
      status: w.status,
      demo: isDemo(w.epistemic_status),
      obligations: w.totals?.obligations?.total ?? 0,
      reported: w.totals?.obligations?.reported ?? 0,
      noReport: w.totals?.no_report?.value ?? null,
      areaHa: toHa(w.area?.total_microha ?? null),
      daysLeft: w.days_left ?? null,
    }))
    .sort((a, b) => (a.windowFrom ?? '9999').localeCompare(b.windowFrom ?? '9999'));
}

/** Ключ полива в адресе: одна работа шаблона живёт в разных кампаниях. */
export function workKey(work: Pick<IrrigationWork, 'campaignId' | 'workDefId'>): string {
  return `${work.campaignId}:${work.workDefId}`;
}

/** Положение окна полива относительно даты состояния. */
export type WateringState = 'done' | 'active' | 'upcoming' | 'unknown';

/**
 * Окно считается по датам, а не по статусу кампании: кампания бывает
 * «активной» задолго до своего окна, а на прошлую дату (`as_of`) статус
 * сервера не откатывается. Дата состояния `ref` — ISO-день.
 */
export function wateringState(
  work: Pick<IrrigationWork, 'windowFrom' | 'windowTo'>,
  ref: string,
): WateringState {
  if (!work.windowFrom && !work.windowTo) return 'unknown';
  if (work.windowTo && work.windowTo < ref) return 'done';
  if (work.windowFrom && work.windowFrom > ref) return 'upcoming';
  return 'active';
}

/**
 * Полив по умолчанию: тот, чьё окно идёт на дату состояния (самый ранний из
 * идущих), иначе последний закрывшийся, иначе первый в хронологии.
 * Запрошенный адресом — если он есть в списке; неизвестный ключ не выдумывает
 * работу, а падает на умолчание. Без даты состояния — по статусу кампании.
 */
export function pickWork(
  works: readonly IrrigationWork[],
  requested: string | null,
  ref?: string | null,
): IrrigationWork | null {
  if (works.length === 0) return null;
  const asked = requested ? works.find((w) => workKey(w) === requested) : undefined;
  if (asked) return asked;
  const isActive = (w: IrrigationWork) => (ref ? wateringState(w, ref) === 'active' : w.status === 'active');
  const isDone = (w: IrrigationWork) => (ref ? wateringState(w, ref) === 'done' : w.status === 'completed');
  const active = works.find(isActive);
  if (active) return active;
  const done = works.filter(isDone);
  if (done.length > 0) return done[done.length - 1]!;
  return works[0]!;
}

export interface CycleChoice {
  cycle: string;
  name: string;
  status: SeasonCycles['items'][number]['status'];
  /** Начало окна цикла; `null` — не названо. */
  windowFrom: string | null;
}

export function cycleChoices(reply: SeasonCycles): CycleChoice[] {
  return reply.items.map((row) => ({
    cycle: row.cycle,
    name: `${cropName(row.crop_code, row.crop_name)} ${row.season_label}`,
    status: row.status,
    windowFrom: row.window_from ?? null,
  }));
}

/**
 * Цикл по умолчанию — активный; из нескольких активных — начавшийся раньше
 * (до даты состояния `ref`, если она дана): у только что открытого цикла
 * поливов ещё нет, а идущий давно — в разгаре. Запрошенный адресом побеждает.
 */
export function pickCycle(
  choices: readonly CycleChoice[],
  requested: string | null,
  ref?: string | null,
): CycleChoice | null {
  if (choices.length === 0) return null;
  const asked = choices.find((c) => c.cycle === requested);
  if (asked) return asked;
  const active = choices.filter((c) => c.status === 'active');
  const started = ref ? active.filter((c) => !c.windowFrom || c.windowFrom <= ref) : active;
  const pool = started.length > 0 ? started : active;
  const earliest = [...pool].sort((a, b) => (a.windowFrom ?? '9999').localeCompare(b.windowFrom ?? '9999'))[0];
  return earliest ?? choices[0]!;
}
